// Offline data-entry helpers (Fase 4 — incremental, NO local recompute).
//
// Design: the downloaded snapshot is an immutable baseline. Offline edits are
// stored as deltas in the `sync_queue` (IndexedDB). On each offline load we replay
// the queue onto a fresh copy of the baseline so edits show. Progress / stage
// enabling / promotion are NOT recomputed offline — the server recomputes them on
// sync (that local recompute is the separate Fase 5).
//
// Framework-agnostic (no Vue) so it can be unit-tested.

// Local-only ids for offline-created results/comments: negative so they never
// collide with server ids and are easy to recognize at sync time.
let tempCounter = 0
export function nextTempId() {
  tempCounter += 1

  return -(Date.now() * 1000 + tempCounter)
}

function findIndicator(evaluation, indicatorId) {
  const domains = (evaluation && evaluation.domains) || []
  let found = null
  domains.some(domain =>
    (domain.outcomes || []).some(outcome => {
      found = (outcome.indicators || []).find(i => i.id === indicatorId)

      return !!found
    }))

  return found || null
}

function applyScore(evaluation, change) {
  const indicator = findIndicator(evaluation, change.indicatorId)
  const stage = indicator && indicator.stages && indicator.stages[change.stageIndex]
  const principle = stage && stage[change.principle]
  if (!principle || !principle.result) return

  if (change.action === 'deleted') {
    principle.result.id = null
    principle.result.options__id = null
    principle.result.options__score = null
  } else {
    principle.result.id = change.action === 'created' ? change.resultTempId : change.resultId
    principle.result.options__id = change.options
    principle.result.options__score = change.options__score
  }
}

function applyComment(evaluation, change) {
  // Only indicator-level comments are replayed here; general comments live in a
  // separate parent structure and are out of scope for this increment.
  if (change.isGeneral) return
  const indicator = findIndicator(evaluation, change.indicatorId)
  const stage = indicator && indicator.stages && indicator.stages[change.stageIndex]
  if (!stage) return
  if (!stage.comments) stage.comments = []

  if (change.op === 'add') {
    stage.comments.push(change.comment)
  } else if (change.op === 'remove') {
    const idx = stage.comments.findIndex(c => c.id === change.commentId)
    if (idx !== -1) stage.comments.splice(idx, 1)
  } else if (change.op === 'edit') {
    const idx = stage.comments.findIndex(c => c.id === change.commentId)
    if (idx !== -1) stage.comments.splice(idx, 1, change.comment)
  }
}

/**
 * Stamp each principle's result with its baseline value BEFORE replaying deltas,
 * so an offline edit can tell whether the user reverted to the original (→ drop
 * the delta) or genuinely changed it. Call on the pristine snapshot, pre-replay.
 */
export function stampBaseline(evaluation) {
  const domains = (evaluation && evaluation.domains) || []
  domains.forEach(domain =>
    (domain.outcomes || []).forEach(outcome =>
      (outcome.indicators || []).forEach(indicator =>
        (indicator.stages || []).forEach(stage => {
          ['quality', 'expansion'].forEach(key => {
            const principle = stage[key]
            if (principle && principle.result) {
              principle.result._baselineOptionId = principle.result.options__id != null
                ? principle.result.options__id
                : null
              principle.result._baselineResultId = principle.result.id != null
                ? principle.result.id
                : null
            }
          })
        }))))

  return evaluation
}

// --- Demographics -----------------------------------------------------------
//
// The 16 fields that count toward completion — the server counts all model
// fields except {id, evaluation_line, professions, notes} (utils.py
// get_demographics_completion_data / DEMOGRAPHICS_TOTAL = 16). Kept in sync
// with that list so the offline % matches the server after a local edit.
export const DEMOGRAPHICS_COUNTED_FIELDS = [
  'number_of_students', 'early_childhood', 'children', 'youth', 'male', 'female',
  'students_without_disabilities', 'students_with_disabilities',
  'students_with_mult_disabilities_not_visually', 'students_with_visual_disabilities',
  'students_with_mult_disabilities_and_visually', 'students_with_deafblindness',
  'percentage_of_minority_groups', 'number_of_teachers',
  'number_of_other_professionals', 'total_staff',
]
const DEMOGRAPHICS_TOTAL = 16

// Mirror of the server's completion calc: a field counts as completed when it
// holds a value (0 counts — the server checks `is not None`, not truthiness).
export function computeDemographicsCompletion(data) {
  const d = data || {}
  const completed = DEMOGRAPHICS_COUNTED_FIELDS.reduce((n, key) => {
    const v = d[key]
    const missing = v === null || v === undefined || v === ''
      || (typeof v === 'number' && Number.isNaN(v))

    return missing ? n : n + 1
  }, 0)

  return {
    completed,
    total: DEMOGRAPHICS_TOTAL,
    percentage: Math.round((completed / DEMOGRAPHICS_TOTAL) * 100 * 100) / 100,
  }
}

/**
 * Merge an offline demographics edit (`form`) onto the snapshot baseline and
 * recompute completion locally, so the card shows the edited values and an
 * up-to-date % without a server round-trip. Returns null if there is neither a
 * baseline nor a form (nothing downloaded / nothing to show).
 */
export function applyDemographicsDelta(baseline, form) {
  if (!baseline && !form) return null
  const merged = { ...(baseline || {}), ...(form || {}) }
  merged.demographics_completion = computeDemographicsCompletion(merged)

  return merged
}

/**
 * Replay queued offline changes onto an evaluation payload (mutates + returns it).
 * @param {object} evaluation the snapshot's `data.evaluation`
 * @param {Array} queue the sync_queue entries for this assessment (in order)
 */
export function applyQueueToEvaluation(evaluation, queue) {
  if (!evaluation || !Array.isArray(queue)) return evaluation
  queue.forEach(change => {
    if (change.type === 'score') applyScore(evaluation, change)
    else if (change.type === 'comment') applyComment(evaluation, change)
  })

  return evaluation
}

/**
 * Body for `sync-offline` / `offline-discarded`, built from a line's queue.
 * Grouped by kind so the back applies scores/comments/demographics in order.
 * @param {number} lineId evaluation_line id
 * @param {Array} queue the sync_queue entries for this assessment
 */
export function buildSyncPayload(lineId, queue) {
  const list = Array.isArray(queue) ? queue : []
  const demographicsDelta = list.find(c => c.type === 'demographics')

  return {
    evaluation_line_id: lineId,
    scores: list.filter(c => c.type === 'score'),
    comments: list.filter(c => c.type === 'comment'),
    demographics: demographicsDelta ? demographicsDelta.form : null,
  }
}
